// Plan 08-04 / VISUAL-PASS — sprite-atlas loader keyed by ThemeKey.
//
// ThemeProvider (and the dev /cell-swatch route) call
// `ensureThemeAtlasLoaded(themeKey)` AFTER applyTheme + BEFORE handing the
// descriptor to children, so the sprite-sheet texture cache is warm when the
// first LetterCubeSprite / PieceLayer sprite constructs. Without this, every
// `Assets.get('atlas').textures[frameName]` lookup misses and the cells/pieces
// render through the Texture.EMPTY fallback (the dormant-atlas bug this fixes:
// the prior `preloadThemeAssets(descriptor)` resolved the RELATIVE
// `descriptor.assets.sprite_atlas` ("atlas.json") against the page URL → 404,
// and was never called anyway).
//
// PixiJS v8 `Assets.load({alias:'atlas', src:'/themes/<key>/atlas.json'})`
// parses the TexturePacker-shaped JSON into a Spritesheet, auto-loads the
// `meta.image` (sprites.png) resolved relative to the JSON URL, and binds the
// per-frame textures — so `Assets.get('atlas').textures[frameName]` resolves
// synchronously thereafter.

import { Assets, loadTextures } from 'pixi.js'
import type { ThemeKey } from '../theme/descriptor'

// CSP FIX: Pixi's image loader decodes textures on a Web Worker created from a
// `blob:` URL by default. The production CSP has no `worker-src` directive, so
// it falls back to `script-src 'self' …` (no `blob:`) and BLOCKS that worker —
// the atlas then fails to load ("[Assets] Asset id atlas was not found in the
// Cache"). The atlas is a single tiny 256×384 PNG, so decode it on the main
// thread instead (no worker, no CSP relaxation). `connect-src 'self'` already
// allows the same-origin atlas.json / sprites.png fetch.
if (loadTextures?.config) {
  loadTextures.config.preferWorkers = false
}

/** Canonical alias every consumer (LetterLayer / PieceLayer) looks up. */
const ATLAS_ALIAS = 'atlas'

// Single 'atlas' alias rebound per active theme (cube faces + piece player
// colours are baked per theme, so the sprite-sheet image itself swaps on a
// theme change — not just the CSS vars / Pixi uniforms).
let loadedThemeKey: ThemeKey | null = null
let inflight: { key: ThemeKey; promise: Promise<void> } | null = null

/**
 * Ensure the active theme's sprite atlas is loaded + bound to the 'atlas'
 * alias. Idempotent for the already-loaded theme; rebinds (unload → reload)
 * when the theme changes. Returns a Promise that resolves once the texture
 * cache is warm.
 */
export function ensureThemeAtlasLoaded(themeKey: ThemeKey): Promise<void> {
  if (loadedThemeKey === themeKey) return Promise.resolve()
  if (inflight && inflight.key === themeKey) return inflight.promise

  const atlasPath = `/themes/${themeKey}/atlas.json`
  const promise = (async (): Promise<void> => {
    // Rebind the alias when switching themes (the previous theme's baked
    // sprite-sheet must be released so 'atlas' resolves to the new image).
    if (loadedThemeKey !== null && loadedThemeKey !== themeKey) {
      try {
        await Assets.unload(ATLAS_ALIAS)
      } catch {
        // Best-effort: a failed unload still lets the reload proceed.
      }
    }
    await Assets.load({ alias: ATLAS_ALIAS, src: atlasPath })
    loadedThemeKey = themeKey
  })()
    .catch((err: unknown): never => {
      // Eject the in-flight record so a retry can re-attempt the load.
      if (inflight?.key === themeKey) inflight = null
      throw err
    })
    .then((): void => {
      if (inflight?.key === themeKey) inflight = null
    })

  inflight = { key: themeKey, promise }
  return promise
}

/** Test-only: reset the loader state between cases. */
export function _resetAtlasLoaderForTesting(): void {
  loadedThemeKey = null
  inflight = null
}
